import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import React from 'react';
import { RuleJsonModel } from '../domain/collection/rule/FlowDomains';
import NestRuleClient from '../client/rule/NestRuleClient';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';
import { Syntax } from '@trlogic/ui-pack';
import _ from 'lodash';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { query } from '@trlogic/client-js';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import ExpressionBridge from '../util/ExpressionBridge';
import WorkflowNodeUtil from '../util/WorkflowNodeUtil';
import { EventToken, ResolutionToken, RuleTypes } from '../domain/collection/rule/RuleDomains';
import { changeRuleDirty } from '../redux/actions/RuleActions';
import { ActionType } from '../constant/Constants';
import Query = query.Query;
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';

class RuleService {
  private readonly expressionBridge: ExpressionBridge;

  constructor(expressionBridge: ExpressionBridge) {
    this.expressionBridge = expressionBridge;
  }

  private flowClient: NestRuleClient = GatewayContainer.getInstance().get(GatewayNames.RULE_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  public saveFlow = async (flow: RuleJsonModel, isUpdate: boolean, folder: EnrichedComponentCategory) => {
    try {
      if (flow.name == undefined || flow.name.trim().length < 1) {
        MessageUtil.error({
          summary: 'Rule',
          detail: <div>{LanguageUtil.get({ id: 'messages.error.nameempty', defaultMessage: 'Name cannot be empty' })}</div>,
        });
        return false;
      }
      if (!isUpdate) {
        const isExist = await this.flowClient.isExist(flow.name);
        if (isExist) {
          MessageUtil.alreadyExist('Rule');
          return false;
        }
      }
      const transaction = (WorkflowNodeUtil.getTransaction(flow.workflow)?.value as EventToken)?.status;
      if (!transaction) {
        MessageUtil.error({
          summary: 'Rule',
          detail: (
            <div>{LanguageUtil.get({ id: 'messages.error.selectTransactionToSave', defaultMessage: 'Please select transaction to save the rule.' })}</div>
          ),
        });
        return false;
      }
      const parsedFlow: RuleJsonModel = _.cloneDeep(flow);
      parsedFlow.rule = await this.parseExpression(parsedFlow.rule);
      parsedFlow.workflow = await this.parseExpression(parsedFlow.workflow);
      parsedFlow.id = parsedFlow.name;
      if (parsedFlow.type === RuleTypes.RESOLUTION) {
        parsedFlow.score = null;
      } else {
        const resolutionScore = (WorkflowNodeUtil.getResolution(parsedFlow.workflow)?.value as ResolutionToken | undefined)?.score;
        if (resolutionScore !== undefined && resolutionScore !== null) {
          parsedFlow.score = resolutionScore;
        }
      }
      await this.flowClient.save(parsedFlow);

      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.RULE, folder.id, flow.name);
      }

      MessageUtil.success({
        summary: 'Rule',
        detail: (
          <div>
            <b>{flow.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'Saved' })}
          </div>
        ),
      });
      dispatch(changeRuleDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: 'Rule',
        detail: (
          <div>
            <b>{flow.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'can not save' })}
          </div>
        ),
      });
      return false;
    }
  };

  public deployFlow = async (rule: RuleJsonModel) => {
    return this.flowClient
      .deploy(rule.name)
      .then(() => {
        return MessageUtil.success({
          summary: 'Rule',
          detail: (
            <div>
              <b>{rule.name}</b> {LanguageUtil.get({ id: 'messages.deployed', defaultMessage: 'Deployed' })}
            </div>
          ),
        });
      })
      .catch((error: unknown) => {
        if (DeployErrorUtil.show(error, 'Rule')) {
          return;
        }
        return MessageUtil.error({
          summary: 'Rule',
          detail: (
            <div>
              <b>{rule.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeployed', defaultMessage: 'can not deploy' })}
            </div>
          ),
        });
      });
  };

  public undeployFlow = (name: string) => {
    return this.flowClient
      .undeploy(name)
      .then(() => {
        return MessageUtil.success({
          summary: 'Rule',
          detail: (
            <div>
              <b>{name}</b> {LanguageUtil.get({ id: 'messages.undeployed', defaultMessage: 'undeployed' })}
            </div>
          ),
        });
      })
      .catch((error: unknown) => {
        if (DeployErrorUtil.show(error, 'Rule')) {
          return;
        }
        return MessageUtil.error({
          summary: 'Rule',
          detail: (
            <div>
              <b>{name} </b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: 'can not undeploy' })}
            </div>
          ),
        });
      });
  };

  public removeFlow = async (name: string): Promise<boolean> => {
    try {
      const response: any = await this.flowClient.remove(name);
      if (response.code) {
        return false;
      }
      MessageUtil.success({
        summary: 'Rule',
        detail: (
          <div>
            <b>{name}</b> {LanguageUtil.get({ id: 'messages.deployed', defaultMessage: 'removed' })}
          </div>
        ),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, 'Rule')) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.RULE));
      }
      MessageUtil.error({
        summary: 'Rule',
        detail: (
          <div>
            <b>{name}</b> {LanguageUtil.get({ id: 'messages.cannotdeployed', defaultMessage: 'can not remove' })}
          </div>
        ),
      });
      return false;
    }
  };

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs: any = await this.flowClient.bulkAction(ids, type);
    return logs;
  }

  public async parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(await this.expressionBridge.stop(syntax));
  }

  public getCount(query: Query): Promise<any> {
    return this.flowClient.count(query);
  }
}

export default RuleService;
